/**
 * Pagination — the strip that sits under every dense list (DS-20…25).
 *
 * Server-side by contract: it owns no state, it reports intent. `page` is
 * 1-based to match `PageQuery` in @nova/shared.
 *
 * Renders nothing when the result set fits on one page, so short lists are not
 * padded with a strip that has no page to go to.
 */
import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '@nova/shared';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

/** 25 / 50 / 100 / 200 — bounded by MAX_PAGE_SIZE so a list can never ask the API for more. */
const PAGE_SIZE_OPTIONS = [DEFAULT_PAGE_SIZE, 50, 100, MAX_PAGE_SIZE];

/** DS-51 — a number inside Arabic copy stays LTR and monospaced. */
function Figure({ children }: { children?: ReactNode }) {
  return <span className="ltr-num font-mono font-bold text-slate-700">{children}</span>;
}

/**
 * DS-51 — the figures stay LTR and monospaced wherever the sentence puts them,
 * so `Figure` travels with the text rather than the word order being fixed here.
 */
function defaultRangeLabel(from: number, to: number, total: number): ReactNode {
  return (
    <Trans
      i18nKey="ui.pagination.range"
      ns="common"
      values={{ from, to, total }}
      components={[<Figure key="range" />, <Figure key="total" />]}
    />
  );
}

export interface PaginationProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 1-based, as the API expects. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size selector entirely. */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Defaults to the translated "Showing 1–25 of 340". */
  rangeLabel?: (from: number, to: number, total: number) => ReactNode;
  pageSizeLabel?: string;
}

function NavButton({
  label,
  disabled,
  onClick,
  children,
  className,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        `inline-flex h-6 w-6 items-center justify-center rounded border border-slate-300 bg-white
         text-slate-600 transition hover:bg-slate-50 hover:text-slate-800
         disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-slate-50
         max-lg:h-9 max-lg:w-9`,
        className,
      )}
    >
      {children}
    </button>
  );
}

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(function Pagination(
  {
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    rangeLabel = defaultRangeLabel,
    pageSizeLabel: pageSizeLabelProp,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const pageSizeLabel = pageSizeLabelProp ?? t('ui.pagination.pageSize');
  const navLabels = {
    first: t('ui.pagination.first'),
    prev: t('ui.pagination.prev'),
    next: t('ui.pagination.next'),
    last: t('ui.pagination.last'),
  };
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  // A single page has nothing to navigate and nothing to resize: the strip would be
  // four dead buttons and a size selector that changes nothing. Take it out entirely.
  if (totalPages <= 1) return null;

  const current = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);
  const atStart = current <= 1;
  const atEnd = current >= totalPages;

  // Radix positions its popper by direction; the document flips with the language (DS-50).
  const dir: 'rtl' | 'ltr' = typeof document !== 'undefined' && document.dir === 'rtl' ? 'rtl' : 'ltr';

  return (
    <div
      ref={ref}
      className={cn(
        'flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-white px-2 py-1.5 text-xs',
        'max-lg:px-2 max-lg:py-2',
        className,
      )}
      {...rest}
    >
      {/* NFR-05 — how many rows per page is a desktop decision. On a phone the
          strip has room for the range and the two steps that move it, and the
          selector would push them off the edge. */}
      {onPageSizeChange ? (
        <div className="flex items-center gap-1.5 max-lg:hidden">
          <span className="label-micro">{pageSizeLabel}</span>
          <SelectPrimitive.Root
            dir={dir}
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectPrimitive.Trigger
              data-testid="pagination-page-size"
              aria-label={pageSizeLabel}
              className="inline-flex h-6 items-center gap-1 rounded border border-slate-300 bg-white px-1.5
                         font-mono text-xs text-slate-700 transition hover:bg-slate-50"
            >
              <SelectPrimitive.Value />
              <SelectPrimitive.Icon>
                <ChevronDown className="h-3 w-3 text-slate-400" />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>
            <SelectPrimitive.Portal>
              <SelectPrimitive.Content
                position="popper"
                sideOffset={4}
                className="z-50 overflow-hidden rounded border border-slate-300 bg-white shadow-lg animate-zoomIn"
              >
                <SelectPrimitive.Viewport className="p-0.5">
                  {pageSizeOptions.map((option) => (
                    <SelectPrimitive.Item
                      key={option}
                      value={String(option)}
                      className="flex cursor-pointer items-center justify-center rounded px-3 py-1
                                 font-mono text-xs text-slate-700 outline-none
                                 data-[highlighted]:bg-blue-50 data-[state=checked]:font-bold data-[state=checked]:text-blue-700"
                    >
                      <SelectPrimitive.ItemText>{option}</SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  ))}
                </SelectPrimitive.Viewport>
              </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
          </SelectPrimitive.Root>
        </div>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-3 max-lg:w-full max-lg:justify-between max-lg:gap-2">
        <span className="text-slate-500">{rangeLabel(from, to, total)}</span>
        {/* DS-50 — the chevrons point at reading order, so they mirror with the document.
            Jump-to-end is a desktop luxury: on a phone the two steps and the
            counter are what fit, and the counter is what the thumb aims past. */}
        <div className="flex items-center gap-1">
          <NavButton
            label={navLabels.first}
            disabled={atStart}
            onClick={() => onPageChange(1)}
            className="max-sm:hidden"
          >
            <ChevronsLeft data-testid="pagination-first" className="flip-rtl h-4 w-4" />
          </NavButton>
          <NavButton label={navLabels.prev} disabled={atStart} onClick={() => onPageChange(current - 1)}>
            <ChevronLeft data-testid="pagination-prev" className="flip-rtl h-4 w-4" />
          </NavButton>
          <span className="ltr-num px-1 font-mono text-slate-500">
            {current} / {totalPages}
          </span>
          <NavButton label={navLabels.next} disabled={atEnd} onClick={() => onPageChange(current + 1)}>
            <ChevronRight data-testid="pagination-next" className="flip-rtl h-4 w-4" />
          </NavButton>
          <NavButton
            label={navLabels.last}
            disabled={atEnd}
            onClick={() => onPageChange(totalPages)}
            className="max-sm:hidden"
          >
            <ChevronsRight data-testid="pagination-last" className="flip-rtl h-4 w-4" />
          </NavButton>
        </div>
      </div>
    </div>
  );
});
